/**
 * CMS full coverage — the section registry. Single source of truth for WHICH
 * sections exist, what fields each one has, and who may edit them; both the
 * platform-admin editor (`pages/admin/Cms.tsx`) and the reseller editor
 * (`pages/reseller/Content.tsx`) render from this file (filtered to
 * `resellerEditable` sections for the reseller view) instead of each hand-
 * rolling its own list. The fallback (placeholder) TEXT lives in
 * `./fallbacks.ts` — this file is metadata only (labels, shapes, field lists).
 *
 * `resellerEditable` only gates the RESELLER editor's section list — the admin
 * editor always shows every section (including reseller-editable ones): a
 * platform-scope row for a `landing.*` key acts as the default template every
 * reseller's own override then wins over (`getPublicMerged`), so the admin
 * view is also where the platform sets that shared default.
 *
 * Legal pages ARE here now (2026-07-20). They were originally excluded in favour
 * of a `legal_documents` table with versioning + acceptance semantics — but that
 * table was only ever scaffolded (0 rows, no service reads it, no admin UI), so
 * in practice terms/privacy/refund/cookies were hardcoded in the frontend and
 * NOT editable by anyone without a deploy. Routing them through this registry
 * makes them editable today with the machinery that already works. If versioned
 * legal documents with acceptance tracking are ever actually built, move them
 * back out.
 *
 * Footer PROSE is covered as of 2026-07-20 — column headings, copyright,
 * payments label and status line, per surface (`company.footer*` = the ITCity
 * home, `shared.footer*` = the platform footer, `landing.footer*` = resellers).
 * The three namespaces are deliberately separate because itcity.ltd and
 * possignin.com both resolve to the SAME platform CMS scope, so shared keys
 * would make one edit change both sites. Footer LINKS stay in code: routes are
 * typed, and a CMS-supplied destination could ship a dead link.
 *
 * Nav labels, route-bound button labels, brand-interpolated wordmarks, Blog's
 * CATEGORY_META, the decorative POS mock panel, and the pricing
 * monthly/yearly toggle labels are deliberately excluded too — see
 * docs/superpowers/sdd/cms-coverage-report.md.
 */

export interface CmsFieldDef {
  /** Field key inside the section's value map (SINGLE) or list item (LIST). */
  name: string;
  label: string;
  /** Render as a multi-row textarea (a sentence/paragraph) instead of a single-line one. */
  multiline?: boolean;
  /**
   * LIST-item field only: this field's value is a `string[]` rather than a
   * plain string (e.g. a bullet list, or a blog post's paragraphs) — edited as
   * one textarea, split into entries by `listDelimiter` (default: one per line).
   */
  list?: boolean;
  /** Only meaningful when `list` is true. 'blank' splits on blank lines (paragraphs); 'line' (default) splits on every line (short bullets). */
  listDelimiter?: 'line' | 'blank';
}

export interface CmsSectionDef {
  key: string;
  label: string;
  kind: 'single' | 'list';
  fields: CmsFieldDef[];
  /** LIST sections only: singular noun for the add/remove UI ("feature", "post"). Default "item". */
  itemLabel?: string;
  /** LIST sections only: item cap shown in the UI. Defaults to 24 (matches the API's MAX_LIST_ITEMS). */
  maxItems?: number;
  /** May a reseller edit this section on its own scope? Default false (platform-only). */
  resellerEditable?: boolean;
  /** Small editor hint shown under the section header (e.g. a slug-stability warning). */
  note?: string;
}

export interface CmsPageDef {
  key: string;
  label: string;
  sections: CmsSectionDef[];
}

/**
 * ITCity's own copies of the four legal pages. They render on BOTH platform hosts from one
 * shared `legal.*` key set, so an edit for possignin.com rewrites itcity.ltd too.
 *
 * ⚠ Legal text usually SHOULD be identical — both sites are operated by the same company —
 * so these exist to make divergence possible and DELIBERATE, not to encourage it. Left
 * blank (the default) each falls back to the Possignin text, so the two stay in lockstep
 * until someone explicitly writes an ITCity version. Never half-fill one: a partly-written
 * ITCity terms page would mix two documents.
 */
const LEGAL_KINDS = [
  { kind: 'terms', label: 'Terms' },
  { kind: 'privacy', label: 'Privacy' },
  { kind: 'refund', label: 'Refund' },
  { kind: 'cookies', label: 'Cookies' },
] as const;

function itcityLegalSections(): CmsSectionDef[] {
  return LEGAL_KINDS.flatMap(({ kind, label }): CmsSectionDef[] => {
    const cap = kind.charAt(0).toUpperCase() + kind.slice(1);
    return [
      {
        key: `company.legal${cap}Meta`,
        label: `${label} — heading`,
        kind: 'single',
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'updated', label: 'Last updated' },
          { name: 'intro', label: 'Intro', multiline: true },
        ],
        note: `itcity.ltd/${kind} only. Blank → the Possignin ${label.toLowerCase()} text renders unchanged.`,
      },
      {
        key: `company.legal${cap}`,
        label: `${label} — sections`,
        kind: 'list',
        itemLabel: 'section',
        maxItems: 24,
        fields: [
          { name: 'heading', label: 'Heading' },
          { name: 'body', label: 'Body', multiline: true, list: true, listDelimiter: 'blank' },
        ],
        note: `itcity.ltd/${kind} only. Leave empty to reuse the Possignin sections — do not half-fill it.`,
      },
    ];
  });
}

export const CMS_REGISTRY: CmsPageDef[] = [
  {
    key: 'company',
    label: 'itcitysoft.com — Company (ITCity)',
    sections: [
      {
        key: 'company.hero',
        label: 'Hero',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
      },
      {
        key: 'company.heroBadges',
        label: 'Hero trust badges',
        kind: 'list',
        itemLabel: 'badge',
        maxItems: 8,
        fields: [{ name: 'text', label: 'Text' }],
      },
      {
        key: 'company.featuresIntro',
        label: '"What Possignin does" intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'company.features',
        label: 'Feature grid',
        kind: 'list',
        itemLabel: 'feature',
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position — reordering here reorders the icons too.',
      },
      {
        key: 'company.productsIntro',
        label: '"ITCity products" intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
        ],
      },
      {
        key: 'company.products',
        label: 'Products showcase',
        kind: 'list',
        itemLabel: 'product',
        maxItems: 6,
        fields: [
          { name: 'name', label: 'Name' },
          { name: 'tag', label: 'Tag' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icon, live/soon status, and link stay fixed in code and pair with these by position.',
      },
      {
        key: 'company.details',
        label: 'Registered company details',
        kind: 'single',
        fields: [
          { name: 'legalName', label: 'Registered legal name' },
          { name: 'registration', label: 'Registration / trade licence no.' },
          { name: 'address', label: 'Registered address', multiline: true },
          { name: 'phone', label: 'Phone' },
          { name: 'email', label: 'Email' },
          { name: 'hours', label: 'Business hours' },
        ],
        note: 'Required by the payment gateway (SSLCommerz) on the merchant site. Replace the placeholder values with the real registered details before launch.',
      },
      {
        key: 'company.management',
        label: 'Management / leadership',
        kind: 'list',
        itemLabel: 'person',
        maxItems: 8,
        fields: [
          { name: 'name', label: 'Name' },
          { name: 'role', label: 'Role / designation' },
          { name: 'bio', label: 'Short bio', multiline: true },
        ],
        note: 'Required by the payment gateway (SSLCommerz). Placeholder people are shown until you replace them.',
      },
      {
        key: 'company.about',
        label: 'About ITCity',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body1', label: 'Body — paragraph 1', multiline: true },
          { name: 'body2', label: 'Body — paragraph 2', multiline: true },
        ],
      },
      {
        key: 'company.blogIntro',
        label: 'Blog — intro',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'ITCity’s OWN newsroom (itcitysoft.com / itcity.ltd /blog). Separate from the Possignin product blog on purpose — both sites resolve to the same platform CMS scope, so a shared key would make one edit change both sites.',
      },
      {
        key: 'company.blogPosts',
        label: 'Blog — posts',
        kind: 'list',
        itemLabel: 'post',
        maxItems: 24,
        fields: [
          { name: 'slug', label: 'Slug (URL)' },
          { name: 'title', label: 'Title' },
          { name: 'excerpt', label: 'Excerpt', multiline: true },
          { name: 'date', label: 'Date' },
          { name: 'category', label: 'Category' },
          { name: 'readMins', label: 'Read time (minutes)' },
          { name: 'body', label: 'Body paragraphs', list: true, listDelimiter: 'blank' },
        ],
        note: 'Company/newsroom posts for ITCity. Changing a slug breaks existing links to that post.',
      },
      {
        key: 'company.footerTagline',
        label: 'Footer tagline',
        kind: 'single',
        fields: [{ name: 'tagline', label: 'Tagline' }],
      },
      {
        key: 'company.footerHeadings',
        label: 'Footer — column headings',
        kind: 'single',
        fields: [
          { name: 'product', label: 'Column 1 heading' },
          { name: 'company', label: 'Column 2 heading' },
          { name: 'legal', label: 'Column 3 heading' },
        ],
        note: 'Headings only — the LINKS inside each column are fixed in code so they can never point at a dead route.',
      },
      {
        key: 'company.footerMeta',
        label: 'Footer — copyright, payments & status',
        kind: 'single',
        fields: [
          { name: 'copyright', label: 'Copyright line' },
          { name: 'paymentsLabel', label: 'Label above the payment-methods banner' },
          { name: 'statusLabel', label: 'Status line' },
        ],
        note: 'The ITCity company page (itcitysoft.com / itcity.ltd home). possignin.com has its own copy — see "Shared chrome".',
      },
      {
        key: 'company.plansPreview',
        label: 'Live plans strip — headline',
        kind: 'single',
        fields: [{ name: 'headline', label: 'Headline' }],
      },

      // ── ITCity's own /pricing, /features and /about ──────────────────────────
      // Those three pages resolve on BOTH platform hosts and used to read one shared
      // set of keys, so an edit made for possignin.com silently rewrote the same page
      // on itcity.ltd. Same reason `company.blog*` exists separately from `blog.*`.
      // Left blank, each falls back to the Possignin copy, so nothing changes until
      // ITCity is actually given its own words.
      {
        key: 'company.pricingIntro',
        label: 'Pricing — intro',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'itcity.ltd/pricing only. Blank → falls back to the Possignin pricing intro.',
      },
      {
        key: 'company.pricingAssurances',
        label: 'Pricing — assurance cards',
        kind: 'list',
        itemLabel: 'assurance',
        maxItems: 6,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'itcity.ltd/pricing only. Leave the list empty to reuse Possignin’s cards.',
      },
      {
        key: 'company.pricingContactLine',
        label: 'Pricing — contact line',
        kind: 'single',
        fields: [{ name: 'text', label: 'Text' }],
        note: 'itcity.ltd/pricing only.',
      },
      {
        key: 'company.featuresIntro',
        label: 'Features — intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'itcity.ltd/features only. Blank → falls back to the Possignin features intro.',
      },
      {
        key: 'company.featureGroups',
        label: 'Features — groups',
        kind: 'list',
        itemLabel: 'group',
        maxItems: 12,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'itcity.ltd/features only. Leave empty to reuse Possignin’s groups.',
      },
      {
        key: 'company.featuresDataBanner',
        label: 'Features — data banner',
        kind: 'single',
        fields: [{ name: 'text', label: 'Text' }],
        note: 'itcity.ltd/features only.',
      },
      {
        key: 'company.aboutIntro',
        label: 'About — intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body1', label: 'Body — paragraph 1', multiline: true },
          { name: 'body2', label: 'Body — paragraph 2', multiline: true },
          { name: 'body3', label: 'Body — paragraph 3', multiline: true },
        ],
        note: 'itcity.ltd/about only. Blank → falls back to the Possignin about intro.',
      },
      {
        key: 'company.aboutValues',
        label: 'About — values',
        kind: 'list',
        itemLabel: 'value',
        maxItems: 8,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'itcity.ltd/about only. Leave empty to reuse Possignin’s values.',
      },
      {
        key: 'company.aboutCta',
        label: 'About — closing CTA',
        kind: 'single',
        fields: [
          { name: 'locationTag', label: 'Location tag' },
          { name: 'headline', label: 'Headline' },
        ],
        note: 'itcity.ltd/about only.',
      },
      {
        key: 'company.contactInfo',
        label: 'Contact — details',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
          { name: 'phone', label: 'Phone' },
          { name: 'address', label: 'Address' },
          { name: 'hours', label: 'Office hours' },
        ],
        note: 'itcity.ltd/contact only. Blank fields fall back to the Possignin contact details; the email always comes from the ITCity support address in Settings → Branding.',
      },
      ...itcityLegalSections(),
    ],
  },
  {
    key: 'product',
    label: 'possignin.com — Product landing',
    sections: [
      {
        key: 'product.hero',
        label: 'Hero',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'Leave headline blank to keep the built-in two-colour styled headline.',
      },
      {
        key: 'product.announcement',
        label: 'Hero announcement bar',
        kind: 'single',
        fields: [
          { name: 'text', label: 'Text' },
          { name: 'href', label: 'Link' },
        ],
      },
      {
        key: 'product.heroBadges',
        label: 'Hero trust badges',
        kind: 'list',
        itemLabel: 'badge',
        maxItems: 8,
        fields: [{ name: 'text', label: 'Text' }],
      },
      {
        key: 'product.trustStats',
        label: 'Trust stats bar',
        kind: 'list',
        itemLabel: 'stat',
        maxItems: 6,
        fields: [
          { name: 'value', label: 'Value' },
          { name: 'label', label: 'Label' },
        ],
      },
      {
        key: 'product.featuresIntro',
        label: 'Features intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'product.features',
        label: 'Feature grid',
        kind: 'list',
        itemLabel: 'feature',
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position.',
      },
      {
        key: 'product.builtForBdIntro',
        label: '"Built for Bangladesh" intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'product.builtForBd',
        label: '"Built for Bangladesh" points',
        kind: 'list',
        itemLabel: 'point',
        maxItems: 6,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position.',
      },
      {
        key: 'product.howItWorksIntro',
        label: '"How it works" intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
        ],
      },
      {
        key: 'product.howItWorks',
        label: '"How it works" steps',
        kind: 'list',
        itemLabel: 'step',
        maxItems: 6,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Step numbers stay fixed in code (1, 2, 3…) based on position.',
      },
      {
        key: 'product.resellerBand',
        label: 'Reseller partner band',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'product.testimonial',
        label: 'Testimonial',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'quote', label: 'Quote', multiline: true },
          { name: 'attribution', label: 'Attribution' },
        ],
      },
      {
        key: 'product.faqIntro',
        label: 'FAQ heading',
        kind: 'single',
        fields: [{ name: 'heading', label: 'Heading' }],
      },
      {
        key: 'product.faq',
        label: 'FAQ',
        kind: 'list',
        itemLabel: 'question',
        maxItems: 12,
        fields: [
          { name: 'q', label: 'Question' },
          { name: 'a', label: 'Answer', multiline: true },
        ],
      },
      {
        key: 'product.finalCta',
        label: 'Final call to action',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'product.plansPreview',
        label: 'Live plans strip — headline',
        kind: 'single',
        fields: [{ name: 'headline', label: 'Headline' }],
      },
    ],
  },
  {
    key: 'pricing',
    label: 'Pricing',
    sections: [
      {
        key: 'pricing.intro',
        label: 'Intro',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'Subhead falls back to brand-aware copy per host when left blank.',
      },
      {
        key: 'pricing.assurances',
        label: 'Assurance cards',
        kind: 'list',
        itemLabel: 'assurance',
        maxItems: 6,
        resellerEditable: true,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'pricing.contactLine',
        label: 'Contact line',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'text', label: 'Text' }],
        note: 'Only this sentence is editable — the "Talk to us" / "become a reseller" links stay fixed in code.',
      },
    ],
  },
  {
    key: 'features',
    label: 'Features page',
    sections: [
      {
        key: 'features.intro',
        label: 'Intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
      },
      {
        key: 'features.groups',
        label: 'Feature groups',
        kind: 'list',
        itemLabel: 'group',
        maxItems: 9,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'items', label: 'Bullet points', list: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position.',
      },
      {
        key: 'features.dataBanner',
        label: 'Data-ownership banner',
        kind: 'single',
        fields: [{ name: 'text', label: 'Text' }],
      },
    ],
  },
  {
    key: 'about',
    label: 'About page',
    sections: [
      {
        key: 'about.intro',
        label: 'Intro',
        kind: 'single',
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'body1', label: 'Body — paragraph 1', multiline: true },
          { name: 'body2', label: 'Body — paragraph 2', multiline: true },
          { name: 'body3', label: 'Body — paragraph 3', multiline: true },
        ],
      },
      {
        key: 'about.values',
        label: 'Values',
        kind: 'list',
        itemLabel: 'value',
        maxItems: 6,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position.',
      },
      {
        key: 'about.cta',
        label: 'Call to action',
        kind: 'single',
        fields: [
          { name: 'locationTag', label: 'Location tag' },
          { name: 'headline', label: 'Headline' },
        ],
      },
    ],
  },
  {
    key: 'blog',
    label: 'Blog / Newsroom',
    sections: [
      {
        key: 'blog.intro',
        label: 'Intro',
        kind: 'single',
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
      },
      {
        key: 'blog.posts',
        label: 'Posts',
        kind: 'list',
        itemLabel: 'post',
        maxItems: 24,
        fields: [
          { name: 'slug', label: 'Slug (URL)' },
          { name: 'title', label: 'Title' },
          { name: 'excerpt', label: 'Excerpt', multiline: true },
          { name: 'date', label: 'Date' },
          { name: 'category', label: 'Category' },
          { name: 'readMins', label: 'Read time (minutes)' },
          { name: 'body', label: 'Body paragraphs', list: true, listDelimiter: 'blank' },
        ],
        note: 'Changing a slug breaks existing links to that post. Category should match an existing category (Announcements / Playbook / Product / Partners) — an unrecognised one still renders safely with a default icon.',
      },
      {
        key: 'blog.notFound',
        label: '"Story not found" text',
        kind: 'single',
        fields: [{ name: 'text', label: 'Text' }],
      },
    ],
  },
  {
    key: 'landing',
    label: 'Reseller homepage (reseller-editable)',
    sections: [
      {
        key: 'landing.hero',
        label: 'Hero',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
        ],
        note: 'Leave headline blank to keep the built-in "Run your shop with {your name}" styled headline.',
      },
      {
        key: 'landing.cta',
        label: 'Hero call-to-action button',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'text', label: 'Button text' }],
      },
      {
        key: 'landing.heroBadges',
        label: 'Hero trust badges',
        kind: 'list',
        itemLabel: 'badge',
        maxItems: 8,
        resellerEditable: true,
        fields: [{ name: 'text', label: 'Text' }],
      },
      {
        key: 'landing.featuresIntro',
        label: 'Features section headline',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'headline', label: 'Headline' }],
        note: 'The "Why {your name}" eyebrow above it stays templated in code.',
      },
      {
        key: 'landing.features',
        label: 'Feature grid',
        kind: 'list',
        itemLabel: 'feature',
        maxItems: 12,
        resellerEditable: true,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
        note: 'Icons stay fixed in code and pair with these by position.',
      },
      {
        key: 'landing.trustStats',
        label: 'Trust stats strip',
        kind: 'list',
        itemLabel: 'stat',
        maxItems: 4,
        resellerEditable: true,
        fields: [
          { name: 'value', label: 'Big value (e.g. "7 days")' },
          { name: 'label', label: 'Small label under it' },
        ],
      },
      {
        key: 'landing.howItWorksIntro',
        label: '"How it works" headline',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'eyebrow', label: 'Eyebrow' },
          { name: 'headline', label: 'Headline' },
        ],
      },
      {
        key: 'landing.howItWorks',
        label: '"How it works" steps',
        kind: 'list',
        itemLabel: 'step',
        maxItems: 5,
        resellerEditable: true,
        fields: [
          { name: 'title', label: 'Title' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'landing.plansPreview',
        label: 'Plans strip headline',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'headline', label: 'Headline' }],
        note: 'The plan cards themselves are live catalog data (your published packages) — edit them under My packages.',
      },
      {
        key: 'landing.faqIntro',
        label: 'FAQ heading',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'heading', label: 'Heading' }],
      },
      {
        key: 'landing.faq',
        label: 'FAQ entries',
        kind: 'list',
        itemLabel: 'question',
        maxItems: 10,
        resellerEditable: true,
        fields: [
          { name: 'q', label: 'Question' },
          { name: 'a', label: 'Answer', multiline: true },
        ],
      },
      {
        key: 'landing.ctaStrip',
        label: 'Bottom call-to-action strip',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'body', label: 'Body', multiline: true },
        ],
      },
      {
        key: 'landing.footerTagline',
        label: 'Footer tagline',
        kind: 'single',
        resellerEditable: true,
        fields: [{ name: 'tagline', label: 'Tagline' }],
      },
      {
        key: 'landing.footerHeadings',
        label: 'Footer — column headings',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'product', label: 'Column 1 heading' },
          { name: 'legal', label: 'Column 2 heading' },
        ],
        note: 'Headings only — the LINKS inside each column are fixed in code so they can never point at a dead route.',
      },
      {
        key: 'landing.footerMeta',
        label: 'Footer — copyright & status',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'copyright', label: 'Copyright line' },
          { name: 'statusLabel', label: 'Status line' },
        ],
        note: 'Leave the copyright blank to show your own brand name and the current year automatically.',
      },
    ],
  },
  {
    key: 'contact',
    label: 'Contact page (reseller-editable)',
    sections: [
      {
        key: 'contact.info',
        label: 'Contact details',
        kind: 'single',
        resellerEditable: true,
        fields: [
          { name: 'headline', label: 'Headline' },
          { name: 'subhead', label: 'Subhead', multiline: true },
          { name: 'phone', label: 'Phone (blank = use the brand support phone)' },
          { name: 'address', label: 'Office address', multiline: true },
          { name: 'hours', label: 'Opening hours (e.g. "Sat–Thu, 10:00–18:00")' },
        ],
        note: 'The email card always shows the brand support email. Blank phone/address/hours rows are hidden on the page.',
      },
    ],
  },
  {
    key: 'legal',
    label: 'Legal — terms / privacy / refund / cookies',
    sections: (['terms', 'privacy', 'refund', 'cookies'] as const).flatMap((kind) => {
      const nice = { terms: 'Terms of Service', privacy: 'Privacy Policy', refund: 'Refund Policy', cookies: 'Cookie Policy' }[kind];
      return [
        {
          key: `legal.${kind}Meta`,
          label: `${nice} — header`,
          kind: 'single' as const,
          fields: [
            { name: 'title', label: 'Title' },
            { name: 'updated', label: 'Last updated (e.g. "14 June 2026")' },
            { name: 'intro', label: 'Intro', multiline: true },
          ],
          note: 'Update the "last updated" date whenever you change the sections below.',
        },
        {
          key: `legal.${kind}`,
          label: `${nice} — sections`,
          kind: 'list' as const,
          itemLabel: 'section',
          maxItems: 24,
          fields: [
            { name: 'heading', label: 'Heading' },
            { name: 'body', label: 'Paragraphs (one per blank-line-separated block)', multiline: true, list: true, listDelimiter: 'blank' as const },
          ],
          note: 'Headings become the on-this-page anchors — renaming one changes its link.',
        },
      ];
    }),
  },
  {
    key: 'shared',
    label: 'Shared chrome',
    sections: [
      {
        key: 'shared.footerTagline',
        label: 'Platform footer tagline',
        kind: 'single',
        fields: [{ name: 'tagline', label: 'Tagline' }],
        note: 'Shown on the platform-brand footer (possignin.com, and every non-home page on itcitysoft.com / itcity.ltd). The ITCity home page has its own — see "itcitysoft.com — Company". Resellers have their own too — see "Reseller homepage".',
      },
      {
        key: 'shared.footerHeadings',
        label: 'Platform footer — column headings',
        kind: 'single',
        fields: [
          { name: 'product', label: 'Column 1 heading' },
          { name: 'company', label: 'Column 2 heading' },
          { name: 'legal', label: 'Column 3 heading' },
        ],
        note: 'Headings only — the LINKS inside each column are fixed in code so they can never point at a dead route.',
      },
      {
        key: 'shared.footerMeta',
        label: 'Platform footer — copyright, payments & status',
        kind: 'single',
        fields: [
          { name: 'copyright', label: 'Copyright line' },
          { name: 'paymentsLabel', label: 'Label above the payment-methods banner' },
          { name: 'statusLabel', label: 'Status line' },
        ],
      },
    ],
  },
];
